import { useDraggable } from "@dnd-kit/core";
import { CSS, type Transform } from "@dnd-kit/utilities";
import type { DateTime } from "luxon";
import { type FC, type KeyboardEvent, type MouseEvent, useCallback, useMemo } from "react";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import type { DepartmentLocation, RecurringShift } from "../../../../utils/models";
import type { ScheduleDayAtLocationData } from "../utils";
import { ClickableShiftCell, type ClickableShiftCellProps } from "./ClickableShiftCell";

// We take the (stable, useCallback-backed) parent handlers plus the per-cell data and bind them here,
// instead of receiving per-cell closures created inline in DroppableCell's map. Those inline closures
// changed identity on every render and defeated ClickableShiftCell's React.memo during drag.
type Props = Omit<ClickableShiftCellProps, "onShiftClick" | "onShiftKeyDown"> & {
	day: ScheduleDayAtLocationData;
	isDraggingInContext: boolean;
	onCtrlPressedChange?: (isCtrlPressed: boolean) => void;
	recurringShift: RecurringShift;
	clickedLocation: DepartmentLocation;
	onShiftClick: (recurringShift: RecurringShift, event: MouseEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	onShiftKeyDown: (recurringShift: RecurringShift, event: KeyboardEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
};
export type DraggableShiftData = { day: ScheduleDayAtLocationData; nurseId: number; recurringShiftId: number | undefined };

export const DraggableShift: FC<Props> = ({
	day,
	isDraggingInContext,
	onCtrlPressedChange,
	onShiftClick,
	onShiftKeyDown,
	recurringShift,
	clickedLocation,
	...clickableShiftProps
}) => {
	const draggableShiftData: DraggableShiftData = useMemo(
		() => ({
			nurseId: clickableShiftProps.nurseId,
			recurringShiftId: clickableShiftProps.recurringShiftId,
			day,
		}),
		[day, clickableShiftProps.nurseId, clickableShiftProps.recurringShiftId],
	);

	const handleShiftClick = useCallback(
		(event: MouseEvent<HTMLElement>) => onShiftClick(recurringShift, event, day.dayDate, clickedLocation),
		[onShiftClick, recurringShift, day.dayDate, clickedLocation],
	);
	const handleShiftKeyDown = useCallback(
		(event: KeyboardEvent<HTMLElement>) => onShiftKeyDown(recurringShift, event, day.dayDate, clickedLocation),
		[onShiftKeyDown, recurringShift, day.dayDate, clickedLocation],
	);

	const { chosenDepartment } = useChosenDepartment();
	const {
		attributes,
		listeners,
		setNodeRef,
		transform: initialTransform,
		isDragging,
	} = useDraggable({
		id: `${clickableShiftProps.recurringShiftId}|${day.dayDate.toISO()}`,
		data: draggableShiftData,
		disabled: chosenDepartment.disable_edit_master_schedule,
	});

	const transform = initialTransform
		? ({
				...initialTransform,
				/**
				 * Used to fix a bug where the dragged item would slightly scale in the Y direction on drag start when hovering on large cells.
				 */
				scaleY: 1,
			} as Transform)
		: null;

	const style = {
		// Convert the transform into CSS.  dnd‑kit docs recommend using transform for performance
		transform: transform ? CSS.Transform.toString(transform) : undefined,
		zIndex: isDragging ? 10 : undefined,
		opacity: isDragging ? 0.8 : 1,
		width: "100%",
	};

	return (
		<div ref={setNodeRef} {...listeners} {...attributes} style={style}>
			<ClickableShiftCell
				{...clickableShiftProps}
				onShiftClick={handleShiftClick}
				onShiftKeyDown={handleShiftKeyDown}
				isDraggingContext={isDraggingInContext}
				isDraggingSelf={isDragging}
				isCopying={false}
				referenceDate={day.dayDate}
			/>
		</div>
	);
};
